import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Leaf, AlertCircle, Folder, Users, GitBranch, CheckSquare, Zap, FileText, BarChart3, Columns, MessageSquare, Timer, MapPin, GitFork, BarChart2, TrendingUp, Spline, ShieldAlert, FileSpreadsheet, Workflow, LayoutGrid } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TOOL_META = {
  staffing_assessment: { name: "Staffing Diagnostic", icon: Users, color: "bg-blue-100 text-blue-700" },
  vsm_builder: { name: "Value Stream Map", icon: GitBranch, color: "bg-amber-100 text-amber-700" },
  five_s_audit: { name: "5S Audit", icon: CheckSquare, color: "bg-purple-100 text-purple-700" },
  kaizen_charter: { name: "Kaizen Charter", icon: Zap, color: "bg-green-100 text-green-700" },
  sipoc_mapper: { name: "SIPOC", icon: Columns, color: "bg-indigo-100 text-indigo-700" },
  voc_kano: { name: "VoC + Kano", icon: MessageSquare, color: "bg-rose-100 text-rose-700" },
  time_study: { name: "Time Study", icon: Timer, color: "bg-cyan-100 text-cyan-700" },
  waste_walk: { name: "Waste Walk", icon: MapPin, color: "bg-orange-100 text-orange-700" },
  rca_5whys_fishbone: { name: "5 Whys + Fishbone", icon: GitFork, color: "bg-teal-100 text-teal-700" },
  pareto_analyzer: { name: "Pareto", icon: BarChart2, color: "bg-yellow-100 text-yellow-700" },
  maturity_assessment: { name: "Maturity", icon: TrendingUp, color: "bg-emerald-100 text-emerald-700" },
  spaghetti_diagram: { name: "Spaghetti", icon: Spline, color: "bg-amber-100 text-amber-700" },
  pfmea: { name: "PFMEA", icon: ShieldAlert, color: "bg-red-100 text-red-700" },
  a3_problem_solving: { name: "A3", icon: FileSpreadsheet, color: "bg-slate-100 text-slate-700" },
  dmaic_charter: { name: "DMAIC", icon: Workflow, color: "bg-blue-100 text-blue-700" },
  yamazumi: { name: "Yamazumi", icon: BarChart3, color: "bg-fuchsia-100 text-fuchsia-700" },
  impact_effort: { name: "Impact × Effort", icon: LayoutGrid, color: "bg-lime-100 text-lime-700" },
};

function StaffingProjectView({ project }) {
  const diag = project.diagnostic;
  if (!diag) return <p className="text-sage-500 text-sm">Diagnostic not yet generated.</p>;
  const stats = diag.summary_stats || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Total FTE", stats.total_current_fte || 0],
          ["Required FTE", stats.total_required_fte || 0],
          ["Headcount Δ", `${(stats.headcount_delta || 0) > 0 ? "+" : ""}${stats.headcount_delta || 0}`],
          ["Saving / Mo", `$${((stats.potential_saving || 0) / 1000).toFixed(0)}K`],
        ].map(([l, v]) => (
          <div key={l} className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">{l}</p><p className="font-heading text-lg font-bold text-sage-900">{v}</p></div>
        ))}
      </div>
      {diag.priority_actions?.length > 0 && (
        <div>
          <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2">Top focus stages</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {diag.priority_actions.map((s, i) => (
              <div key={i} className="border border-sage-200 rounded-md p-3">
                <p className="font-semibold text-sage-900 text-sm">#{i + 1} {s.name}</p>
                <p className="text-xs text-sage-500 mt-1">Req: {s.required_fte} · Current: {s.current_fte} · {s.priority}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VsmProjectView({ project }) {
  const d = project.tool_data || {};
  const cur = d.current_flow || {};
  const fut = d.future_flow || {};
  const blockCount = (cur.nodes || []).filter(n => n.type === "process").length;
  const futureCount = (fut.nodes || []).filter(n => n.type === "process").length;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Family", d.scope?.product_family || "—"],
          ["Volume / month", String(d.scope?.volume || 0)],
          ["Current blocks", blockCount],
          ["Future blocks", futureCount],
        ].map(([l, v]) => (
          <div key={l} className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">{l}</p><p className="font-heading text-base font-bold text-sage-900 truncate">{v}</p></div>
        ))}
      </div>
      {(d.improvement?.ideas || []).length > 0 && (
        <div>
          <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2">Improvement ideas</h4>
          <ul className="space-y-1">
            {d.improvement.ideas.map((idea, i) => (
              <li key={i} className="bg-sage-50 rounded-md p-2 text-xs flex items-center gap-2">
                <span className="font-semibold text-sage-900 flex-1">{idea.title}</span>
                <span className="bg-white border border-sage-200 rounded px-1.5 py-0.5 text-sage-600">Owner: {idea.owner || "—"}</span>
                <span className="bg-white border border-sage-200 rounded px-1.5 py-0.5 text-sage-600">{idea.impact}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FiveSProjectView({ project }) {
  const d = project.tool_data || {};
  const PILLARS = ["sort", "set", "shine", "standardise", "sustain"];
  const totals = PILLARS.map(k => {
    const items = d.questionnaire?.[k] || [];
    const scores = d.pillars?.[k]?.scores || [];
    const sum = scores.reduce((a, s) => a + (Number(s) || 0), 0);
    const total = items.length * 5;
    return { k, sum, total, pct: total > 0 ? (sum / total) * 100 : 0 };
  });
  const sumAll = totals.reduce((a, t) => a + t.sum, 0);
  const maxAll = totals.reduce((a, t) => a + t.total, 0);
  const pct = maxAll > 0 ? (sumAll / maxAll) * 100 : 0;
  const target = d.target_pct || 80;
  return (
    <div className="space-y-3">
      <div className={`rounded-md p-3 ${pct >= target ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
        <p className="font-heading text-2xl font-bold">{pct.toFixed(1)}%</p>
        <p className="text-xs">{pct >= target ? "PASS" : "Below target"} · Domain: {d.domain || "—"} · Area: {d.area || "—"}</p>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {totals.map(t => (
          <div key={t.k} className="bg-white border border-sage-100 rounded-md p-2 text-center">
            <p className="text-xs text-sage-500 uppercase tracking-wider font-semibold capitalize">{t.k.slice(0, 4)}</p>
            <p className="font-heading text-base font-bold text-sage-900">{t.pct.toFixed(0)}%</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function KaizenProjectView({ project }) {
  const d = project.tool_data || {};
  return (
    <div className="space-y-3">
      <div className="bg-sage-50 rounded-md p-3">
        <p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Problem statement</p>
        <p className="text-sm text-sage-800 mt-1">{d.problem || "—"}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Format", d.format || "—"],
          ["Sponsor", d.sponsor || "—"],
          ["Annual impact", `$${d.business_impact || 0}`],
          ["Realised savings", `$${d.realised_savings || 0}`],
        ].map(([l, v]) => (
          <div key={l} className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">{l}</p><p className="font-heading text-base font-bold text-sage-900 truncate">{v}</p></div>
        ))}
      </div>
      {(d.metrics || []).length > 0 && (
        <div>
          <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2">Metrics</h4>
          <table className="w-full text-xs">
            <thead className="bg-sage-50 text-sage-600"><tr><th className="text-left px-2 py-1.5">Metric</th><th className="text-right px-2 py-1.5">Current</th><th className="text-right px-2 py-1.5">Target</th></tr></thead>
            <tbody>{d.metrics.map((m, i) => <tr key={i} className="border-t border-sage-100"><td className="px-2 py-1">{m.name}</td><td className="text-right px-2 py-1">{m.current}</td><td className="text-right px-2 py-1">{m.target}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SipocProjectView({ project }) {
  const d = project.tool_data || {};
  const COLS = [["suppliers", "Suppliers"], ["inputs", "Inputs"], ["process", "Process"], ["outputs", "Outputs"], ["customers", "Customers"]];
  return (
    <div className="space-y-3">
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Process</p><p className="text-sm font-semibold text-sage-900 mt-1">{d.process_name || "—"}</p><p className="text-xs text-sage-500">{d.boundary || ""}</p></div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        {COLS.map(([k, label]) => (
          <div key={k} className="bg-white border border-sage-200 rounded-md p-2">
            <p className="text-xs font-bold uppercase tracking-wider text-sage-700">{label}</p>
            <ul className="space-y-0.5 mt-1">
              {(d[k] || []).map((it, i) => <li key={i} className="text-xs text-sage-800">• {it.text || "—"}</li>)}
              {(d[k] || []).length === 0 && <li className="text-xs text-sage-400 italic">—</li>}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function VocProjectView({ project }) {
  const d = project.tool_data || {};
  const items = d.ctqs || [];
  const gaps = items.map(it => ({ ...it, gap: (it.importance || 0) * Math.max(0, (it.target || 0) - (it.satisfaction_now || 0)) })).sort((a, b) => b.gap - a.gap).slice(0, 5);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Product</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.product || "—"}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Segment</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.segment || "—"}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Sample N</p><p className="font-heading text-2xl font-bold text-sage-900">{d.sample_size || 0}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">CTQs</p><p className="font-heading text-2xl font-bold text-sage-900">{items.length}</p></div>
      </div>
      {gaps.length > 0 && (
        <div>
          <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2">Top opportunity gaps</h4>
          <ul className="space-y-1">{gaps.map((g, i) => <li key={i} className="bg-white border border-sage-200 rounded-md p-2 text-xs flex gap-2 items-center"><span className="font-semibold text-sage-900 flex-1">{g.ctq || g.voc || "—"}</span><span className="font-mono font-bold text-amber-700">gap {g.gap}</span></li>)}</ul>
        </div>
      )}
    </div>
  );
}

function TimeStudyProjectView({ project }) {
  const d = project.tool_data || {};
  const acts = d.activities || [];
  const tot = acts.reduce((a, x) => {
    const m = (x.samples || []).length ? x.samples.reduce((s, v) => s + Number(v || 0), 0) / x.samples.length : 0;
    return a + m;
  }, 0);
  const va = acts.filter(a => a.classification === "VA").reduce((acc, x) => {
    const m = (x.samples || []).length ? x.samples.reduce((s, v) => s + Number(v || 0), 0) / x.samples.length : 0;
    return acc + m;
  }, 0);
  const vaPct = tot ? (va / tot) * 100 : 0;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Process</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.process || "—"}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">AHT (mean)</p><p className="font-heading text-2xl font-bold text-sage-900">{tot.toFixed(1)} <span className="text-sm font-normal text-sage-500">{d.unit || "min"}</span></p></div>
        <div className={`rounded-md p-3 border ${vaPct >= 30 ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}><p className="text-xs uppercase tracking-wider font-semibold">VA Ratio</p><p className="font-heading text-2xl font-bold">{vaPct.toFixed(1)}%</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Activities</p><p className="font-heading text-2xl font-bold text-sage-900">{acts.length}</p></div>
      </div>
    </div>
  );
}

function WasteWalkProjectView({ project }) {
  const d = project.tool_data || {};
  const obs = d.observations || [];
  const totalCost = obs.reduce((a, o) => a + Number(o.cost_impact || 0), 0);
  const high = obs.filter(o => o.severity === "H").length;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Area</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.area || "—"}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Observations</p><p className="font-heading text-2xl font-bold text-sage-900">{obs.length}</p></div>
        <div className="bg-red-50 rounded-md p-3 border border-red-200"><p className="text-xs uppercase tracking-wider font-semibold text-red-800">High severity</p><p className="font-heading text-2xl font-bold text-red-800">{high}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Cost / yr</p><p className="font-heading text-2xl font-bold text-sage-900">${totalCost.toLocaleString()}</p></div>
      </div>
    </div>
  );
}

function RcaProjectView({ project }) {
  const d = project.tool_data || {};
  const cms = d.countermeasures || [];
  const roots = (d.whys || []).filter(w => w.root_cause).length;
  return (
    <div className="space-y-3">
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Problem</p><p className="text-sm text-sage-800 mt-1">{d.problem || "—"}</p></div>
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">5-Why chains</p><p className="font-heading text-2xl font-bold text-sage-900">{(d.whys || []).length}</p></div>
        <div className="bg-red-50 rounded-md p-3 border border-red-200"><p className="text-xs uppercase tracking-wider font-semibold text-red-800">Root causes</p><p className="font-heading text-2xl font-bold text-red-800">{roots}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Countermeasures</p><p className="font-heading text-2xl font-bold text-sage-900">{cms.length}</p></div>
      </div>
    </div>
  );
}

function ParetoProjectView({ project }) {
  const d = project.tool_data || {};
  const sorted = [...(d.categories || [])].sort((a, b) => Number(b.frequency || 0) - Number(a.frequency || 0));
  const total = sorted.reduce((a, x) => a + Number(x.frequency || 0), 0);
  let cum = 0;
  let vital = 0;
  sorted.forEach(it => { cum += Number(it.frequency || 0); if (total && cum / total <= 0.8) vital++; });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Family</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.problem_family || "—"}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Total {d.unit || ""}</p><p className="font-heading text-2xl font-bold text-sage-900">{total.toLocaleString()}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Categories</p><p className="font-heading text-2xl font-bold text-sage-900">{sorted.length}</p></div>
        <div className="bg-green-50 rounded-md p-3 border border-green-200"><p className="text-xs uppercase tracking-wider font-semibold text-green-800">Vital Few</p><p className="font-heading text-2xl font-bold text-green-800">{vital}</p></div>
      </div>
    </div>
  );
}

function MaturityProjectView({ project }) {
  const d = project.tool_data || {};
  const pillars = d.pillars || [];
  const scores = d.scores || {};
  const cur = pillars.length ? pillars.reduce((a, p) => a + (scores[p.id]?.current || 1), 0) / pillars.length : 0;
  const tgt = pillars.length ? pillars.reduce((a, p) => a + (scores[p.id]?.target || 3), 0) / pillars.length : 0;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Scope</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.scope || "—"}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Now</p><p className="font-heading text-2xl font-bold text-sage-900">{cur.toFixed(1)}/5</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Target</p><p className="font-heading text-2xl font-bold text-sage-900">{tgt.toFixed(1)}/5</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Pillars</p><p className="font-heading text-2xl font-bold text-sage-900">{pillars.length}</p></div>
      </div>
    </div>
  );
}

function SpaghettiProjectView({ project }) {
  const d = project.tool_data || {};
  const scale = d.scale || 1; const cycles = d.cycles_per_day || 1;
  const totals = (d.paths || []).map(p => {
    let dist = 0; const pts = p.points || [];
    for (let i = 1; i < pts.length; i++) { const dx = pts[i].x - pts[i - 1].x, dy = pts[i].y - pts[i - 1].y; dist += Math.sqrt(dx * dx + dy * dy); }
    return { ...p, total: dist * scale };
  });
  const cycleD = totals.reduce((a, p) => a + p.total, 0);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Area</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.area || "—"}</p></div>
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Paths</p><p className="font-heading text-2xl font-bold text-sage-900">{totals.length}</p></div>
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Per cycle</p><p className="font-heading text-2xl font-bold text-sage-900">{cycleD.toFixed(0)} m</p></div>
      <div className="bg-amber-50 rounded-md p-3 border border-amber-200"><p className="text-xs uppercase tracking-wider font-semibold text-amber-800">Per day</p><p className="font-heading text-2xl font-bold text-amber-900">{(cycleD * cycles).toFixed(0)} m</p></div>
    </div>
  );
}

function PfmeaProjectView({ project }) {
  const d = project.tool_data || {};
  const items = (d.modes || []).map(m => ({ ...m, _r: Number(m.severity || 0) * Number(m.occurrence || 0) * Number(m.detection || 0) }));
  const above = items.filter(m => m._r >= (d.rpn_threshold || 100));
  const top = items.length ? Math.max(...items.map(m => m._r)) : 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Process</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.process || "—"}</p></div>
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Failure modes</p><p className="font-heading text-2xl font-bold text-sage-900">{items.length}</p></div>
      <div className="bg-red-50 rounded-md p-3 border border-red-200"><p className="text-xs uppercase tracking-wider font-semibold text-red-800">Above threshold</p><p className="font-heading text-2xl font-bold text-red-800">{above.length}</p></div>
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Top RPN</p><p className="font-heading text-2xl font-bold text-sage-900">{top}</p></div>
    </div>
  );
}

function A3ProjectView({ project }) {
  const d = project.tool_data || {};
  const sec = d.sections || {}; const lists = d.lists || {};
  const BOX = ["background", "current_condition", "goal", "root_cause", "countermeasures", "implementation", "verification", "follow_up"];
  const filled = BOX.filter(k => (sec[k] && sec[k].trim()) || (lists[k] || []).some(i => i.text)).length;
  return (
    <div className="space-y-3">
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Title</p><p className="text-sm font-semibold text-sage-900 mt-1">{d.title || "—"}</p></div>
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Boxes filled</p><p className="font-heading text-2xl font-bold text-sage-900">{filled}/8</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Status</p><p className="font-heading text-base font-bold text-sage-900 capitalize">{d.status || "draft"}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Owner</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.owner || "—"}</p></div>
      </div>
    </div>
  );
}

function DmaicProjectView({ project }) {
  const d = project.tool_data || {};
  const phases = d.phases || {};
  const PH = [["define", "D"], ["measure", "M"], ["analyse", "A"], ["improve", "I"], ["control", "C"]];
  const totalDel = PH.reduce((a, [k]) => a + ((phases[k]?.deliverables || []).length), 0);
  const doneDel = PH.reduce((a, [k]) => a + ((phases[k]?.deliverables || []).filter(x => x.done).length), 0);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Project</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.project_name || "—"}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Deliverables</p><p className="font-heading text-2xl font-bold text-sage-900">{doneDel}/{totalDel}</p></div>
        <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Sponsor</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.sponsor || "—"}</p></div>
        <div className="bg-green-50 rounded-md p-3 border border-green-200"><p className="text-xs uppercase tracking-wider font-semibold text-green-800">Benefit / yr</p><p className="font-heading text-base font-bold text-green-800">${(d.estimated_benefit || 0).toLocaleString()}</p></div>
      </div>
      <div className="grid grid-cols-5 gap-1">{PH.map(([k, lab]) => {
        const p = phases[k] || {}; const dels = p.deliverables || [];
        const pct = dels.length ? (dels.filter(x => x.done).length / dels.length) * 100 : 0;
        return (<div key={k} className="bg-white border border-sage-200 rounded-md p-2 text-center"><p className="text-xs text-sage-500">{lab}</p><p className="font-heading text-base font-bold text-sage-900">{pct.toFixed(0)}%</p></div>);
      })}</div>
    </div>
  );
}

function YamazumiProjectView({ project }) {
  const d = project.tool_data || {};
  const takt = (d.demand_per_period > 0) ? Number(d.available_minutes || 0) / Number(d.demand_per_period) : 0;
  const stations = d.stations || [];
  const sumCT = stations.reduce((a, s) => a + (s.elements || []).reduce((b, e) => b + Number(e.time_min || 0), 0), 0);
  const bottlenecks = stations.filter(s => takt > 0 && (s.elements || []).reduce((a, e) => a + Number(e.time_min || 0), 0) > takt).length;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Line</p><p className="font-heading text-base font-bold text-sage-900 truncate">{d.line_name || "—"}</p></div>
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Takt</p><p className="font-heading text-2xl font-bold text-sage-900">{takt.toFixed(2)}</p></div>
      <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Stations</p><p className="font-heading text-2xl font-bold text-sage-900">{stations.length}</p></div>
      <div className={`rounded-md p-3 border ${bottlenecks > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}><p className="text-xs uppercase tracking-wider font-semibold">Bottlenecks</p><p className="font-heading text-2xl font-bold">{bottlenecks}</p></div>
    </div>
  );
}

function ImpactEffortProjectView({ project }) {  const d = project.tool_data || {};
  const ideas = d.ideas || [];
  const place = (i, e) => { const hi = i >= 3, he = e >= 3; if (hi && !he) return "qw"; if (hi && he) return "bb"; if (!hi && !he) return "fi"; return "ts"; };
  const counts = ideas.reduce((acc, it) => { const q = place(it.impact, it.effort); acc[q] = (acc[q] || 0) + 1; return acc; }, {});
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="bg-green-50 rounded-md p-3 border border-green-200"><p className="text-xs uppercase tracking-wider font-semibold text-green-800">Quick Wins</p><p className="font-heading text-2xl font-bold text-green-800">{counts.qw || 0}</p></div>
      <div className="bg-blue-50 rounded-md p-3 border border-blue-200"><p className="text-xs uppercase tracking-wider font-semibold text-blue-800">Big Bets</p><p className="font-heading text-2xl font-bold text-blue-800">{counts.bb || 0}</p></div>
      <div className="bg-purple-50 rounded-md p-3 border border-purple-200"><p className="text-xs uppercase tracking-wider font-semibold text-purple-800">Fill-ins</p><p className="font-heading text-2xl font-bold text-purple-800">{counts.fi || 0}</p></div>
      <div className="bg-red-50 rounded-md p-3 border border-red-200"><p className="text-xs uppercase tracking-wider font-semibold text-red-800">Time Sinks</p><p className="font-heading text-2xl font-bold text-red-800">{counts.ts || 0}</p></div>
    </div>
  );
}

function GenericProjectView({ project }) {
  const d = project.tool_data || {};
  const entries = Object.entries(d).filter(([, v]) => v != null && (typeof v !== "object" || (Array.isArray(v) && v.length > 0)));
  const arrays = entries.filter(([, v]) => Array.isArray(v));
  const scalars = entries.filter(([, v]) => !Array.isArray(v));
  return (
    <div className="space-y-3">
      {scalars.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {scalars.slice(0, 8).map(([k, v]) => (
            <div key={k} className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">{k.replace(/_/g, " ")}</p><p className="font-heading text-base font-bold text-sage-900 truncate">{typeof v === "object" ? JSON.stringify(v).slice(0, 30) : String(v) || "—"}</p></div>
          ))}
        </div>
      )}
      {arrays.map(([k, v]) => (
        <div key={k} className="bg-white border border-sage-200 rounded-md p-3">
          <p className="text-xs uppercase tracking-wider font-semibold text-sage-700">{k.replace(/_/g, " ")} ({v.length})</p>
        </div>
      ))}
    </div>
  );
}

const VIEWERS = {
  staffing_assessment: StaffingProjectView,
  vsm_builder: VsmProjectView,
  five_s_audit: FiveSProjectView,
  kaizen_charter: KaizenProjectView,
  sipoc_mapper: SipocProjectView,
  voc_kano: VocProjectView,
  time_study: TimeStudyProjectView,
  waste_walk: WasteWalkProjectView,
  rca_5whys_fishbone: RcaProjectView,
  pareto_analyzer: ParetoProjectView,
  maturity_assessment: MaturityProjectView,
  spaghetti_diagram: SpaghettiProjectView,
  pfmea: PfmeaProjectView,
  a3_problem_solving: A3ProjectView,
  dmaic_charter: DmaicProjectView,
  yamazumi: YamazumiProjectView,
  impact_effort: ImpactEffortProjectView,
};

export default function ClientPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/portal/${token}`)
      .then(r => { setData(r.data); setActiveId(r.data.projects[0]?.id || null); })
      .catch(e => setError(e?.response?.data?.detail || "Portal not available"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center"><div className="w-6 h-6 border-2 border-sage-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="bg-white border border-[#E2E8E3] rounded-md p-8 max-w-md text-center" data-testid="portal-error">
        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h2 className="font-heading text-lg font-semibold text-sage-900">Portal unavailable</h2>
        <p className="text-sage-500 text-sm mt-1">{error}</p>
      </div>
    </div>
  );

  const branding = data.branding || {};
  const primary = branding.primary_color || "#4B5E4D";
  const firmName = branding.firm_name || "TheKaizenCabinet";
  const logoUrl = branding.logo_url ? (branding.logo_url.startsWith("http") ? branding.logo_url : `${API}${branding.logo_url}`) : null;
  const active = data.projects.find(p => p.id === activeId);
  const Viewer = active ? (VIEWERS[active.tool_type] || GenericProjectView) : null;
  const ActiveIcon = active ? (TOOL_META[active.tool_type]?.icon || Folder) : Folder;

  return (
    <div className="min-h-screen bg-cream" data-testid="client-portal">
      <nav className="border-b border-[#E2E8E3] sticky top-0 z-40" style={{ backgroundColor: primary }}>
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            {logoUrl ? <img src={logoUrl} alt="" className="h-8 bg-white/10 rounded p-1" /> : <Leaf className="w-5 h-5" />}
            <span className="font-heading text-lg font-semibold">{firmName}</span>
            <span className="text-white/70 text-xs hidden sm:block">· Client Portal</span>
          </div>
          <div className="text-xs text-white/70">{branding.hide_powered_by ? "" : "Powered by TheKaizenCabinet"}</div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">{data.company.domain}</p>
          <h1 className="font-heading text-2xl font-semibold text-sage-900">{data.company.name}</h1>
          <p className="text-sage-500 text-sm">{data.company.country}{data.company.department ? ` · ${data.company.department}` : ""} · {data.projects.length} project{data.projects.length !== 1 ? "s" : ""}</p>
        </div>

        {data.projects.length === 0 ? (
          <div className="bg-white border border-dashed border-sage-200 rounded-md p-10 text-center text-sage-400">No projects shared yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
            <aside className="bg-white border border-[#E2E8E3] rounded-md p-2 space-y-1 self-start" data-testid="portal-project-list">
              {data.projects.map(p => {
                const Icon = TOOL_META[p.tool_type]?.icon || Folder;
                const meta = TOOL_META[p.tool_type] || { name: p.tool_type, color: "bg-sage-100 text-sage-700" };
                const isActive = p.id === activeId;
                return (
                  <button key={p.id} data-testid={`portal-project-${p.id}`} onClick={() => setActiveId(p.id)} className={`w-full text-left rounded-md p-3 transition-colors ${isActive ? "bg-sage-50" : "hover:bg-sage-50/50"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-3.5 h-3.5 text-sage-600" />
                      <span className="font-semibold text-sm text-sage-900 truncate">{p.name}</span>
                    </div>
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${meta.color}`}>{meta.name}</span>
                  </button>
                );
              })}
            </aside>

            <section className="bg-white border border-[#E2E8E3] rounded-md p-5">
              {active && Viewer ? (
                <>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-sage-100">
                    <ActiveIcon className="w-5 h-5 text-sage-600" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-sage-500">{TOOL_META[active.tool_type]?.name}</p>
                      <h2 className="font-heading text-lg font-semibold text-sage-900">{active.name}</h2>
                    </div>
                  </div>
                  <Viewer project={active} />
                </>
              ) : <p className="text-sage-500 text-sm">Select a project from the list.</p>}
            </section>
          </div>
        )}

        <p className="text-center text-xs text-sage-400 pt-6 pb-4">{branding.footer_text || "Confidential — For internal use only"} · Read-only · Expires {new Date(data.expires_at).toLocaleDateString()}</p>
      </main>
    </div>
  );
}
